import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { LANG_LABEL } from "../lib/categories";
import { useCategories } from "../lib/categoriesContext";
import { ArticleCard } from "../components/ArticleCard";
import { Search as SearchIcon } from "lucide-react";

export default function Search() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [category, setCategory] = useState(params.get("category") || "");
  const [language, setLanguage] = useState(params.get("language") || "");
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const { categories } = useCategories();

  const fetchResults = (qVal = q, catVal = category, langVal = language) => {
    setLoading(true);
    const p = {};
    if (qVal) p.q = qVal;
    if (catVal) p.category = catVal;
    if (langVal) p.language = langVal;
    api.get("/articles", { params: p }).then((r) => setArticles(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { fetchResults(); /* eslint-disable-next-line */ }, []);

  const submit = (e) => {
    e.preventDefault();
    setParams({ ...(q && { q }), ...(category && { category }), ...(language && { language }) });
    fetchResults();
  };

  return (
    <div data-testid="search-page">
      <section className="border-b border-[#E0D8CE]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="font-mono-label text-[#4A7A3D] mb-3">Recherche</div>
          <h1 className="font-display text-5xl md:text-7xl leading-[1.0] mb-10">Trouve ce que tu cherches.</h1>

          <form onSubmit={submit} className="space-y-6" data-testid="search-form">
            <div className="flex items-center border-b-2 border-[#1A1A18] pb-3 gap-3">
              <SearchIcon size={20} />
              <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ex: cannabis médical, Maurice, NADC, légalisation…" className="bg-transparent w-full text-xl md:text-2xl font-display outline-none placeholder:text-[#4B4B48]/60" autoFocus data-testid="search-input" />
            </div>
            <div className="flex flex-wrap gap-4">
              <div>
                <label className="font-mono-label text-[#4B4B48] block mb-2">Rubrique</label>
                <select value={category} onChange={(e) => setCategory(e.target.value)} className="bg-transparent border-2 border-[#1A1A18] px-4 py-2 font-mono-label" data-testid="search-category-select">
                  <option value="">Toutes les rubriques</option>
                  {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="font-mono-label text-[#4B4B48] block mb-2">Langue</label>
                <select value={language} onChange={(e) => setLanguage(e.target.value)} className="bg-transparent border-2 border-[#1A1A18] px-4 py-2 font-mono-label" data-testid="search-language-select">
                  <option value="">Toutes</option>
                  {Object.entries(LANG_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <button type="submit" className="bg-[#1A1A18] text-[#FAF8F3] px-8 py-2 font-mono-label hover:bg-[#4A7A3D] self-end" data-testid="search-submit">Rechercher</button>
            </div>
          </form>
        </div>
      </section>

      <section className="border-b border-[#E0D8CE]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="font-mono-label text-[#4B4B48] mb-8">
            {loading ? "RECHERCHE EN COURS…" : `${articles.length} ARTICLE${articles.length > 1 ? "S" : ""} TROUVÉ${articles.length > 1 ? "S" : ""}`}
          </div>
          {!loading && articles.length === 0 ? (
            <div className="py-16 text-center" data-testid="search-no-results">
              <p className="font-quote text-2xl text-[#4B4B48] mb-6">Aucun résultat.</p>
              <p className="text-[#4B4B48] mb-8">Essaie avec d'autres mots-clés, ou explore une rubrique :</p>
              <div className="flex flex-wrap gap-2 justify-center">
                {categories.slice(0, 6).map((c) => (
                  <Link key={c.slug} to={`/rubrique/${c.slug}`} className="border border-[#1A1A18] px-4 py-2 font-mono-label hover:bg-[#1A1A18] hover:text-[#FAF8F3]">
                    {c.name}
                  </Link>
                ))}
              </div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {articles.map((a) => <ArticleCard key={a.slug} article={a} />)}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
